import React, { useEffect, useState } from 'react';
import { Alert, Input, InputNumber, Modal, Space, message } from 'antd';
import { ConsignmentEditBatchPayload, ConsignmentItem } from '../../model/consignment';

const genKey = (prefix: string): string =>
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const BATCH_REASON_MAX_CHARS = 120;

export const batchSourceLabel = (sourceType?: string) => {
    switch (sourceType) {
        case 'INITIAL': return 'ฝากเริ่มต้น';
        case 'ADD_STOCK': return 'ฝากเพิ่ม';
        case 'CARRY_FORWARD': return 'ยกยอดจากรอบก่อน';
        default: return sourceType || '-';
    }
};

/** Backend refused because the lot changed under the form (or the Period
 * did): the form is out of date, so it closes and the bill reloads. */
export const isStaleBatchError = (msg: string) =>
    /STALE_BATCH|STALE_PERIOD|ALREADY_CLOSED|ไม่ได้:/.test(msg || '');

interface Props {
    open: boolean;
    item: ConsignmentItem | null;
    periodNo?: number;
    expectedPeriodId?: number;
    onClose: () => void;
    onSave: (payload: ConsignmentEditBatchPayload) => Promise<any>;
    onStale?: () => void;
}

const money = (v: any) => `฿${Number(v || 0).toFixed(2)}`;

/** "แก้ไขล็อต": a lot typed in wrong -- its quantity, shop price or owner
 * payout -- while nothing has happened to it. The product cannot change
 * here: a wrong product is "ยกเลิกรายการ" and a new deposit. */
export const ConsignmentBatchEditModal: React.FC<Props> = ({ open, item, periodNo, expectedPeriodId, onClose, onSave, onStale }) => {
    const [quantity, setQuantity] = useState<number | undefined>(undefined);
    const [sellingPrice, setSellingPrice] = useState<number | undefined>(undefined);
    const [ownerCost, setOwnerCost] = useState<number | undefined>(undefined);
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState('');

    useEffect(() => {
        if (!open || !item) return;
        setQuantity(Number(item.quantity_received));
        setSellingPrice(Number(item.selling_price_per_unit));
        setOwnerCost(Number(item.owner_cost_per_unit));
        setReason('');
        setIdempotencyKey(genKey('edit-batch'));
        // Reset per lot, not on every re-render of the same lot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, item?.id]);

    if (!item) return null;

    const handleSave = async () => {
        if (!Number.isInteger(quantity) || Number(quantity) <= 0) {
            message.error('จำนวนรับฝากต้องเป็นจำนวนเต็มมากกว่า 0');
            return;
        }
        if (sellingPrice === undefined || sellingPrice === null || Number(sellingPrice) < 0) {
            message.error('กรุณาระบุราคาขายต่อหน่วย');
            return;
        }
        if (ownerCost === undefined || ownerCost === null || Number(ownerCost) < 0) {
            message.error('กรุณาระบุยอดจ่ายผู้ฝากต่อหน่วย');
            return;
        }
        if (Number(quantity) === Number(item.quantity_received)
            && Number(sellingPrice) === Number(item.selling_price_per_unit)
            && Number(ownerCost) === Number(item.owner_cost_per_unit)) {
            message.info('ยังไม่ได้เปลี่ยนข้อมูลใด');
            return;
        }
        if (expectedPeriodId === undefined || item.id === undefined) {
            message.error('ไม่พบรอบบิลที่เปิดอยู่ กรุณารีเฟรชข้อมูล');
            return;
        }
        setSaving(true);
        try {
            await onSave({
                batch_id: item.id,
                expected_period_id: expectedPeriodId,
                expected_quantity_received: Number(item.quantity_received),
                expected_selling_price_per_unit: Number(item.selling_price_per_unit),
                expected_owner_cost_per_unit: Number(item.owner_cost_per_unit),
                new_quantity_received: Number(quantity),
                new_selling_price_per_unit: Number(sellingPrice),
                new_owner_cost_per_unit: Number(ownerCost),
                reason: reason.trim(),
                idempotency_key: idempotencyKey
            });
            message.success(`แก้ไขล็อตที่ ${item.batch_no} เรียบร้อย`);
            onClose();
        } catch (error: any) {
            const msg: string = error?.message || 'แก้ไขล็อตไม่สำเร็จ';
            message.error(msg);
            if (isStaleBatchError(msg)) {
                onClose();
                onStale?.();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            className="consignment-batch-edit-modal"
            open={open}
            title={`✏️ แก้ไขล็อตที่ ${item.batch_no ?? '-'}`}
            okText="บันทึกการแก้ไข"
            cancelText="ยกเลิก"
            confirmLoading={saving}
            onOk={handleSave}
            onCancel={saving ? undefined : onClose}
            okButtonProps={{ disabled: saving, 'data-testid': 'btn-save-batch-edit' } as any}
            destroyOnClose
            width={560}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div className="consignment-batch-context" data-testid="batch-edit-context">
                    <div><span>สินค้า</span><strong>{item.product_name}{item.product_code ? ` (${item.product_code})` : ''}</strong></div>
                    <div><span>รอบบิล</span><strong>{periodNo ? `ที่ ${periodNo}` : '-'}</strong></div>
                    <div><span>ล็อตที่</span><strong>{item.batch_no ?? '-'}</strong></div>
                    <div><span>ที่มา</span><strong>{batchSourceLabel(item.source_type)}</strong></div>
                    <div><span>จำนวนรับฝากเดิม</span><strong>{item.quantity_received} {item.product_unit_name || ''}</strong></div>
                    <div><span>คงเหลือปัจจุบัน</span><strong>{item.latest_actual_remaining} {item.product_unit_name || ''}</strong></div>
                    <div><span>ราคาขายเดิม</span><strong>{money(item.selling_price_per_unit)}</strong></div>
                    <div><span>จ่ายผู้ฝากเดิม</span><strong>{money(item.owner_cost_per_unit)}</strong></div>
                </div>
                <Alert type="info" showIcon message="แก้ได้เฉพาะล็อตที่ยังไม่มีการขาย ตรวจนับ คืน หรือจ่ายผู้ฝาก ถ้าเลือกสินค้าผิดให้ใช้ “ลบ/ยกเลิกรายการ” แล้วเพิ่มสินค้าที่ถูกต้องใหม่" />
                <label className="consignment-batch-field">
                    <span>จำนวนรับฝาก</span>
                    <InputNumber min={1} precision={0} value={quantity} style={{ width: '100%' }}
                                 addonAfter={item.product_unit_name || 'ชิ้น'}
                                 onChange={v => setQuantity(v === null ? undefined : Number(v))}
                                 data-testid="input-batch-edit-qty" />
                </label>
                <label className="consignment-batch-field">
                    <span>ราคาขาย/หน่วย</span>
                    <InputNumber min={0} precision={2} value={sellingPrice} style={{ width: '100%' }} addonBefore="฿"
                                 onChange={v => setSellingPrice(v === null ? undefined : Number(v))}
                                 data-testid="input-batch-edit-price" />
                </label>
                <label className="consignment-batch-field">
                    <span>จ่ายคืนผู้ฝาก/หน่วย</span>
                    <InputNumber min={0} precision={2} value={ownerCost} style={{ width: '100%' }} addonBefore="฿"
                                 onChange={v => setOwnerCost(v === null ? undefined : Number(v))}
                                 data-testid="input-batch-edit-cost" />
                </label>
                <label className="consignment-batch-field">
                    <span>เหตุผล/หมายเหตุ (ไม่บังคับ)</span>
                    <Input.TextArea rows={2} maxLength={BATCH_REASON_MAX_CHARS} showCount value={reason}
                                    onChange={e => setReason(e.target.value)} data-testid="input-batch-edit-reason" />
                </label>
            </Space>
        </Modal>
    );
};

export default ConsignmentBatchEditModal;
